import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, GridColumn } from "semantic-ui-react";

import { Dropdown, NavPortal, SearchFilter, useCan } from "components";
import { usePermanentRealTimeUser, useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CreateQuestionModal, EditQuestionModal, QuestionsList } from "modules/QuestionsDashboard/components";
import { useDealerQuestionsQuery, useDefaultQuestionsQuery, useTagsQuery } from "modules/QuestionsDashboard/hooks";
import "modules/QuestionsDashboard/QuestionsDashboard.scss";
import { QUESTION_LISTS } from "modules/QuestionsDashboard/util";
import { isAdmin } from "utils/common";

import type { Question, QuestionID, TagID } from "models";

const matchesFilters = (question: Question, searchTerm: string, tagIds: TagID[]) =>
  (question.title ?? "").toLowerCase().includes(searchTerm.toLowerCase()) && tagIds.every(tagId => question.tags?.some(tag => tag.id === tagId));

export const QuestionsDashboard = () => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<TagID[]>([]);
  const [editedQuestionID, setEditedQuestionID] = useState<QuestionID>();
  const [addQuestionList, setAddQuestionList] = useState<QUESTION_LISTS | null>(null);

  const { user } = usePermanentRealTimeUser();
  const { data: tagsOptions = [] } = useTagsQuery();
  const { data: dealerQuestions = [], isLoading: isDealerQuestionsLoading, refetch: refetchDealerQuestions } = useDealerQuestionsQuery();
  const { data: defaultQuestions = [], isLoading: isDefaultQuestionsLoading, refetch: refetchDefaultQuestions } = useDefaultQuestionsQuery();

  const canListQuestions = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Questions);

  useRegisterNavAction(() => {
    if (!canListQuestions) return;

    refetchDealerQuestions();
    refetchDefaultQuestions();
  });

  const filteredDealerQuestions = dealerQuestions.filter(question => matchesFilters(question, searchTerm, selectedTagIds));
  const filteredDefaultQuestions = defaultQuestions.filter(question => matchesFilters(question, searchTerm, selectedTagIds));

  const handleQuestionCreated = (questionID: QuestionID) => {
    setAddQuestionList(null);
    setEditedQuestionID(questionID);
  };

  return (
    <div className="QuestionsDashboard">
      <NavPortal>
        <SearchFilter value={searchTerm} handleSearchChange={setSearchTerm} />
      </NavPortal>

      <Dropdown
        multiple
        selection
        search
        fluid
        className="questions-tags-filter"
        placeholder={t("v8_filter_by_tags", "Filter by tags")}
        options={tagsOptions}
        value={selectedTagIds}
        onChange={setSelectedTagIds}
      />

      <Grid columns={2}>
        <GridColumn>
          <QuestionsList
            title={t("v8_dealer_questions", "Dealer Questions")}
            questions={filteredDealerQuestions}
            isLoading={isDealerQuestionsLoading}
            onQuestionClick={setEditedQuestionID}
            onAdd={() => setAddQuestionList(QUESTION_LISTS.Dealer)}
          />
        </GridColumn>
        <GridColumn>
          <QuestionsList
            title={t("v8_default_questions", "Default Questions")}
            questions={filteredDefaultQuestions}
            isLoading={isDefaultQuestionsLoading}
            onQuestionClick={setEditedQuestionID}
            onAdd={isAdmin(user?.role_id ?? 0) ? () => setAddQuestionList(QUESTION_LISTS.Default) : undefined}
          />
        </GridColumn>
      </Grid>

      <CreateQuestionModal
        isOpen={addQuestionList !== null}
        isDealerQuestion={addQuestionList === QUESTION_LISTS.Dealer}
        onClose={() => setAddQuestionList(null)}
        onCreate={handleQuestionCreated}
      />

      <EditQuestionModal questionID={editedQuestionID} onClose={() => setEditedQuestionID(undefined)} onOpenQuestion={setEditedQuestionID} />
    </div>
  );
};
